import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, useRoutes } from 'react-router-dom'
import { InteractionType } from '@azure/msal-browser'
import { useMsalAuthentication, AuthenticatedTemplate, useMsal } from '@azure/msal-react'

import './App.css'
import './sass/index.scss'
import { AppContext } from './context/AppContext'
import Constants from './utils/constants'
import HomePage from './components/HomePage'
import Unauthorised from './components/Unauthorised'
import BranchLocator from './components/BranchLocator'
import DatabaseTables from './components/DatabaseTables'
import AutomationContacts from './components/AutomationContacts'
import ContractorManagement from './components/ContractorManagement'
import NewContractor from './components/NewContractor'
import AssignRegion from './components/AssignRegions'
import SCMPeopleList from './components/SCMPeopleList'
import Users from './components/ContractorContact/Users'
// import AddContractors from './components/ContractorContact/AddContractors'
import ContractorContact from './components/ContractorContact'
import WeekendOnCall from "./components/WeekendOnCall";
// import AddWeekend from "./components/WeekendOnCall/AddWeekend";
import Weekends from "./components/WeekendOnCall/Weekends";

interface Claims {
  roles: string[]
}

const App = () => {
  const msal = useMsal()
  const context = useContext(AppContext)
  const [claims, setClaims] = useState({ roles: [] } as Claims)
  const request = useMemo(() => ({ scopes: [context?.apiScope ?? ''] }), [context])

  const { error, login, result } = useMsalAuthentication(InteractionType.Silent, request)

  useEffect(() => {
    if (error) {
      login(InteractionType.Redirect, request)
    }
  }, [error, login, request])

  useEffect(() => {
    if (result && result.idTokenClaims) {
      setClaims(result.idTokenClaims as Claims)
    }
    else if (result === null && msal.accounts.length > 0) {
      setClaims(msal.accounts[0].idTokenClaims as Claims)
    }
  }, [result, msal.accounts])

  const generateRouteChildren = (roles: string[]) => {
    if (roles && roles.length > 0) {
      if (roles.includes(Constants.AdminRole)) {
        return [
          { path: '*', element: <Navigate to='/' /> },
          { path: '/', element: <AutomationContacts /> },
          { path: 'automationContacts', element: <AutomationContacts /> },
          { path: 'contractor-management', element: <ContractorManagement /> },
          { path: 'new-contractor', element: <NewContractor /> },
          { path: 'database-tables', element: <DatabaseTables /> },
          { path: 'branch-locator', element: <BranchLocator /> },
          { path: 'assign-region', element: <AssignRegion /> },
          { path: 'SCMPeopleList', element: <SCMPeopleList /> },
          { path: 'add-standard-contacts', element: <ContractorContact /> },
          { path: 'edit-standard-contacts', element: <ContractorContact /> },
          { path: 'User', element: <Users /> },
          { path: 'add-weekend', element: <WeekendOnCall /> },
          { path: 'edit-weekend', element: <WeekendOnCall /> },
          { path: 'weekendoncall', element: <Weekends /> },
        ]
      }
      else if (roles.includes(Constants.ContractorManagerRole)) {
        return [
          { path: '*', element: <Navigate to='/' /> },
          { path: '/', element: <AutomationContacts /> },
          { path: 'User', element: <Users /> },
          { path: 'automationContacts', element: <AutomationContacts /> },
          { path: 'add-standard-contacts', element: <ContractorContact /> },
          { path: 'edit-standard-contacts', element: <ContractorContact /> },
          { path: 'add-weekend', element: <WeekendOnCall /> },
          { path: 'edit-weekend', element: <WeekendOnCall /> },
        ]
      }
      else {
      return [
        { path: '*', element: <Navigate to='/' /> },
        { path: '/', element: <Unauthorised /> }
      ]
    }
    }
    else {
      return [
        { path: '*', element: <Navigate to='/' /> },
        { path: '/', element: <Unauthorised /> }
      ]
    }
  }

  const routes = {
    path: '/',
    element: <HomePage roles={claims.roles} />,
    children: generateRouteChildren(claims.roles ?? null),
  }

  const routing = useRoutes([routes])

  return (
    <AuthenticatedTemplate>{routing}</AuthenticatedTemplate>
  )
}

export default App